import { Activity, WifiOff } from 'lucide-react';
import { Link } from 'react-router-dom';
import type { SensorDevice } from '../../services/sensorDevices';

interface GenericSensorCardProps {
  device: SensorDevice;
}

/**
 * Fallback card for any sensor type that does not have a bespoke
 * presentational component. Shows the latest value + the type's unit.
 */
export default function GenericSensorCard({ device }: GenericSensorCardProps) {
  const value = typeof device.latest_value === 'number' ? device.latest_value : null;
  const unit = device.type_unit || '';

  return (
    <Link
      to={`/irrigation/sensors/${encodeURIComponent(device.device_id)}`}
      className="block rounded-xl border border-gray-200 bg-white p-4 shadow-sm hover:shadow-md transition-shadow"
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <Activity className="w-4 h-4 text-indigo-600 flex-shrink-0" />
          <div className="min-w-0">
            <div className="text-sm font-semibold text-gray-800 truncate">
              {device.name || device.device_id}
            </div>
            <div className="text-xs text-gray-500 truncate">
              {device.type_display_name || device.type_key}
            </div>
          </div>
        </div>
        {!device.online && <WifiOff className="w-4 h-4 text-gray-400" />}
      </div>

      <div className="mt-3">
        <div className="text-2xl font-bold text-gray-800">
          {value !== null ? `${value}${unit ? ' ' + unit : ''}` : '—'}
        </div>
      </div>

      {device.latest_reading_time && (
        <div className="mt-2 text-[11px] text-gray-400">
          Last: {new Date(device.latest_reading_time).toLocaleString()}
        </div>
      )}
    </Link>
  );
}
